import { Portal } from '@mantine/core';
import { getFullName } from '@zorro/common-utils';
import { Divider } from '@zorro/design-system/v2/Divider';
import { Title } from '@zorro/design-system/v2/Title';
import { singleLineTruncatedTextWithEllipsis } from '@zorro/shared/formatters';
import { MODAL_ZINDEX } from '@zorro/types';
import { Box, Grid, Group, Stack } from '@zorro/web/design-system';
import { CommentsPopover } from '@zorro/web/shared-ui';
import { useAppRouter, useMonolithQuery } from '@zorro/web/ui-utils';
import { ReactNode, useState } from 'react';

import {
  CarrierApplicationModalStepSubmit,
  CarrierApplicationModalStepType,
} from '.';
import { EmployeePageTabType } from '../../EmployeePage/EmployeePageContent';
import { EnrollmentAllowanceCard } from '../../EnrollmentAllowanceCard';
import { FormFooter } from '../../FormFooter';
import { CarrierApplicationModalStepper } from './CarrierApplicationModalStepper';
import {
  getCarrierApplicationModalStepConfig,
  getEmployeeTabRoute,
} from './CarrierApplicationModalUtils';

type Props = {
  step: CarrierApplicationModalStepType;
  children: ReactNode;
  onSubmit: CarrierApplicationModalStepSubmit;
  submitLabel?: string;
  isSubmitDisabled?: boolean;
  employerId: string;
  employeeId: string;
  onboardingPeriodId: string;
  benefitId?: string;
  withReviewCard?: boolean;
  tab: EmployeePageTabType;
};

export const CarrierApplicationStep = ({
  step,
  children,
  onSubmit,
  submitLabel = 'Continue',
  isSubmitDisabled,
  employerId,
  employeeId,
  onboardingPeriodId,
  benefitId,
  withReviewCard = false,
  tab,
}: Props) => {
  const { navigateToRouteAsync } = useAppRouter();
  const { prevStep, nextStep } = getCarrierApplicationModalStepConfig(step);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: employee } = useMonolithQuery({
    method: 'employeesControllerFindOne',
    params: [employeeId],
  });

  const title = employee
    ? `Application process for ${getFullName(employee)}`
    : 'Application process';

  const navigateToStep = (targetStep: CarrierApplicationModalStepType) =>
    navigateToRouteAsync(
      getEmployeeTabRoute(tab),
      [{ employerId, employeeId, onboardingPeriodId }],
      { step: targetStep, ...(benefitId && { benefitId }) }
    );

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const { navigate } = await onSubmit();
      if (navigate) {
        await navigateToStep(nextStep);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (prevStep) {
      void navigateToStep(prevStep);
    }
  };

  return (
    <>
      <Stack mih="calc(100vh - 18.5rem)" pl="xl" pb="5rem" gap={0}>
        <Stack
          style={{ zIndex: MODAL_ZINDEX + 1 }}
          justify="space-between"
          bg="zorroWhite.0"
          pos="sticky"
          mx="-1.5rem"
          px="1.5rem"
          h="9rem"
          pt="md"
          top="0"
        >
          <Group justify="space-between" wrap="nowrap">
            <Box maw="50rem" style={singleLineTruncatedTextWithEllipsis}>
              <Title order={1} display="inline">
                {title}
              </Title>
            </Box>
            <CommentsPopover
              onboardingPeriodId={onboardingPeriodId}
              employerId={employerId}
              employeeId={employeeId}
            />
          </Group>
          <Divider />
          <CarrierApplicationModalStepper
            step={step}
            employerId={employerId}
            employeeId={employeeId}
            onboardingPeriodId={onboardingPeriodId}
            benefitId={benefitId}
            tab={tab}
          />
          <Divider />
        </Stack>
        <Grid overflow="visible" pt="xl">
          <Grid.Col span={{ base: 8, xl: 9 }}>
            <Stack w="100%" gap="xl">
              {children}
            </Stack>
          </Grid.Col>
          {withReviewCard && (
            <Grid.Col
              span={{ base: 4, xl: 3 }}
              h="min-content"
              pos="sticky"
              top="10rem"
            >
              <Group justify="end">
                <EnrollmentAllowanceCard
                  employeeId={employeeId}
                  onboardingPeriodId={onboardingPeriodId}
                  highlightedBenefitId={benefitId}
                  withOnboardingAllowance
                />
              </Group>
            </Grid.Col>
          )}
        </Grid>
      </Stack>
      <Portal target="#application-modal-footer">
        <FormFooter
          primaryLabel={submitLabel}
          primaryButtonProps={{
            onClick: handleSubmit,
            disabled: isSubmitDisabled || isSubmitting,
            loading: isSubmitting,
          }}
          tertiaryLabel={prevStep ? 'Back' : undefined}
          tertiaryButtonProps={{
            onClick: handleBack,
            mr: 'auto',
            disabled: isSubmitting,
          }}
          paperProps={{ pos: 'sticky', bottom: '0' }}
        />
      </Portal>
    </>
  );
};
